import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";


const isSNoColumn = (header) => {
    if (!header) return false;
    const h = header.toLowerCase().replace(/\s|\./g, ""); // remove spaces and dots
    return h === "sno";
};


const extractHeadersAndKeys = (columns) => {
    const headers = [];
    const keys = [];

    const recurse = (cols, parentLabel = "") => {
        cols.forEach(c => {
            // Ignore Action and any S.No variant
            if (c.Header === "Action" || isSNoColumn(c.Header)) return;

            if (c.columns) {
                recurse(c.columns, c.Header);
            } else if (c.accessor) {
                headers.push(parentLabel ? `${parentLabel} - ${c.Header}` : c.Header);
                keys.push(c.accessor);
            }
        });
    };

    recurse(columns);

    // Always prepend dynamic S.No as first column
    //   headers.unshift("S.No");
    //   keys.unshift("__serial__");

    return { headers, keys };
};

export const exportEmployeeToPDF = async (data, companyName, companyDetails, fileName, columns) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const headerHeight = 20;
    // Add company logo (base64 or image URL)
    const imageUrl = process.env.REACT_APP_IMAGE_COMPANY_LOGO;
    let base64Img = null;
    try {
        base64Img = await getBase64FromUrl(imageUrl);
    } catch (error) {
        console.error("Error loading logo:", error);
        // Optionally, set a fallback or skip
    }

    const addHeader = (pageNumber) => {
        // Background box
        doc.setFillColor(255, 255, 255);
        doc.rect(10, 10, 277, headerHeight, "F");

        // Logo - with error handling
        if (base64Img) {
            try {
                doc.addImage(base64Img, "PNG", 12, 12, 16, 16);
            } catch (error) {
                console.error("Error adding image to PDF:", error);
                // Skip adding the image if it fails
            }
        }

        // Company name
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(companyName.toUpperCase(), 15, 22);

        // Company details (address/contact)
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text(companyDetails || "", 15, 29);
    };

    const addFooter = (pageNumber, totalPages) => {
        const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        doc.text(
            `Page ${pageNumber} of ${totalPages}`,
            doc.internal.pageSize.width / 2, // center X
            pageHeight - 10, // 10mm from bottom
            { align: "center" }
        );
    };

    const addWatermark = () => {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        doc.saveGraphicsState();
        // Faint watermark text in the center
        doc.setGState(new doc.GState({ opacity: 0.1 }));
        doc.setFontSize(60);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(150);
        doc.text("AES", pageWidth / 2, pageHeight / 2, { align: "center", baseline: "middle" });
        doc.restoreGraphicsState();
    };

    // const keys = Object.keys(data[0] || {}); // Original keys from the first record
    // const headers = keys.map(key => key.charAt(0).toUpperCase() + key.slice(1)); // For display
    // const tableData = data.map(item => keys.map(key => item[key])); // Use original keys to get values

    const { headers, keys } = extractHeadersAndKeys(columns);
    const tableData = data.map((row, index) =>
        keys.map(key => key === "__serial__" ? index + 1 : (row[key] ?? ""))
    );

    // Create columnStyles for auto-width
    const columnStyles = {};
    keys.forEach((key, index) => {
        if (key === "__serial__") {
            columnStyles[index] = { cellWidth: 10, halign: 'center' }; // S.No always first
        } else {
            columnStyles[index] = { cellWidth: 'auto' };
        }
    });

    autoTable(doc, {
        startY: 35,
        head: [headers.map(h => h.toUpperCase())],
        body: tableData,
        styles: {
            fontSize: 8,
            cellPadding: 3,
            overflow: 'linebreak',
            halign: 'left',
            valign: 'middle',
            lineColor: [220, 220, 220],
            lineWidth: 0.1,
        },
        headStyles: {
            fillColor: [68, 114, 196],
            textColor: [255, 255, 255],
            halign: "center",
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 35, left: 5, right: 5 },
        tableWidth: 'wrap',  // ⚡ key change
        columnStyles: columnStyles,
        horizontalPageBreak: true, // ⚡ key option
        horizontalPageBreakRepeat: 1, // repeat header for each split
        didDrawPage: (data) => {
            const pageNumber = doc.internal.getNumberOfPages();
            addWatermark();
            addHeader(pageNumber);
            addFooter(pageNumber, "{total_pages_count_string}");
        },
    });

    // autoTable(doc, {
    //     startY: 35,
    //     head: [headers.map(h => h.toUpperCase())],
    //     body: tableData,
    //     styles: { fontSize: 8, cellPadding: 3 },
    //     tableWidth: 'wrap',
    //     horizontalPageBreak: true, // ⚡ key option
    //     horizontalPageBreakRepeat: 1, // repeat header for each split
    //     didDrawPage: (data) => {
    //         const pageNumber = doc.internal.getNumberOfPages();
    //         addWatermark();
    //         addHeader(pageNumber);
    //         addFooter(pageNumber, "{total_pages_count_string}");
    //     },
    // });

    if (typeof doc.putTotalPages === "function") {
        doc.putTotalPages("{total_pages_count_string}");
    }
    // Save PDF
    doc.save(`${fileName}.pdf`);
};

// Helper function
const getBase64FromUrl = async (url) => {
    if (!url) {
        throw new Error("Image URL is not provided");
    }
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
    }
    const blob = await response.blob();
    // Check if the blob is a valid image type
    if (!blob.type.startsWith('image/')) {
        throw new Error("Fetched resource is not an image");
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to convert blob to base64"));
        reader.readAsDataURL(blob);
    });
};
